import * as React from "react";
import { useState, useMemo } from "react";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import { ColumnSort, ColumnFilter, TH } from "security-app/components/common";
import { GLCheckbox } from "common/components";
import { usePagination, useSetPagination, useSelectedRows, useSelectedRowsDispatch } from "common/contexts";
import { useFetchSigmaRulesFilterOptions } from "security-app/hooks/useSigmaAPI";
import usePluggableEntityTableElements from "hooks/usePluggableEntityTableElements";
function ListHeader({ rules, visibleColumns, displayRowActions }) {
  const { permissions } = useCurrentUser();
  const pagination = usePagination();
  const setPagination = useSetPagination();
  const [filterOptions, setFilterOptions] = useState({ id: null });
  const { pluggableTableHeaders } = usePluggableEntityTableElements(null, "sigma_rule");
  const canManageRules = useMemo(
    () => rules.some((rule) => isPermitted(permissions, `sigma_rules:edit:${rule.id}`)),
    [permissions, rules]
  );
  const selectedRules = useSelectedRows();
  const selectedRulesDispatch = useSelectedRowsDispatch();
  const { fetchSigmaRulesFilterOptions, fetchingSigmaRulesFilterOptions } = useFetchSigmaRulesFilterOptions();
  useMemo(async () => {
    const filters = await fetchSigmaRulesFilterOptions({ fields: ["level", "status", "enabled"] });
    setFilterOptions(filters);
  }, [fetchSigmaRulesFilterOptions]);
  const levelOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.level)) return [];
    const options = [];
    filterOptions.level.forEach((level) => {
      options.push({ label: level.toString(), value: level.toString(), type: typeof level });
    });
    return options;
  }, [filterOptions]);
  const statusOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.status)) return [];
    const options = [];
    filterOptions.status.forEach((status) => {
      options.push({ label: status.toString(), value: status.toString(), type: typeof status });
    });
    return options;
  }, [filterOptions]);
  const enabledOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.enabled)) return [];
    const options = [];
    filterOptions.enabled.forEach((enabled) => {
      options.push({ label: enabled.toString(), value: enabled.toString(), type: typeof enabled });
    });
    return options;
  }, [filterOptions]);
  const applySort = (field, direction) => {
    setPagination({ ...pagination, orderBy: field, direction });
  };
  const applyFilters = (filterKey, filterValues) => {
    const auxFilters = { ...pagination.filters, [filterKey]: filterValues };
    if (!filterValues || filterValues.length === 0) delete auxFilters[filterKey];
    setPagination({ ...pagination, filters: auxFilters });
  };
  const selectAll = (e) => {
    const auxRules = rules.filter(
      (rule) => isPermitted(permissions, `sigma_rules:edit:${rule.id}`)
    );
    if (e.target.checked) {
      selectedRulesDispatch({ type: "add", payload: auxRules });
    } else {
      selectedRulesDispatch({ type: "remove", payload: auxRules });
    }
  };
  const allSelected = useMemo(
    () => rules.length > 0 && selectedRules.length > 0 && rules.every(
      (rule) => !!selectedRules.find((sRule) => rule.id === sRule.id)
    ),
    [rules, selectedRules]
  );
  const columnIsVisible = (columnName) => visibleColumns.includes(columnName);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !fetchingSigmaRulesFilterOptions && /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, canManageRules ? /* @__PURE__ */ React.createElement(TH, { style: { width: 32 } }, /* @__PURE__ */ React.createElement(GLCheckbox, { checked: allSelected, onChange: selectAll, title: "Select all sigma rules" })) : /* @__PURE__ */ React.createElement(TH, { style: { width: 32 } }), columnIsVisible("title") && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "parsed_rule.title",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    "Rule"
  )), columnIsVisible("level") && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "parsed_rule.level",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "level",
        type: "text",
        filterData: levelOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Level"
    )
  )), columnIsVisible("status") && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "parsed_rule.status",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "status",
        type: "text",
        filterData: statusOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Status"
    )
  )), columnIsVisible("last_run") && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "last_run",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "last_run",
        type: "date",
        filterData: [],
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Last Run"
    )
  )), columnIsVisible("updated_at") && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "updated_at",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "updated_at",
        type: "date",
        filterData: [],
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Last Update"
    )
  )), columnIsVisible("enabled") && /* @__PURE__ */ React.createElement(TH, { style: { width: 65 } }, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "enabled",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "enabled",
        type: "text",
        filterData: enabledOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Enabled"
    )
  )), pluggableTableHeaders, displayRowActions && /* @__PURE__ */ React.createElement(TH, { style: { width: 32 } }))));
}
export default ListHeader;
